import {
  createContext,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';

import { IAllocationClientsByGroup } from '~/services/api/clientAllocation/types';
import { useStoreState } from '~/store/hooks';
import useRole, { IUseRole } from '~/store/user/hooks/useRole';
import { IUseGetEditClientAllocationsResult } from '../hooks/types';
import useGetEditClientAllocations from '../hooks/getAllocations/useGetEditClientAllocations';
import { IParams } from '../types';
import { AllocationMode } from '../../constants/allocationMode';
import normalizeAllocationsByUser from '../../shared/model/normalizeAllocationsByUser';
import { NormalizedTeamMembersAllocations } from '../../shared/model/types';

type EditClientAllocationContextValue = IUseGetEditClientAllocationsResult & {
  actTeamId: string;
  allocationDate: string;
  clinicId: string;
  currentUserRole: IUseRole;
  allocationMode: AllocationMode;
  setAllocationMode: Dispatch<SetStateAction<AllocationMode>>;
  showAllocationModeRadio: boolean;
  locationGroups: IAllocationClientsByGroup[];
  normalizedAllocationsByUser: NormalizedTeamMembersAllocations[];
};

const EditClientAllocationContext = createContext<EditClientAllocationContextValue | null>(null);

interface EditClientAllocationProviderProps {
  children: ReactNode;
}

export const EditClientAllocationProvider = ({
  children,
}: EditClientAllocationProviderProps): ReactElement => {
  const [allocationMode, setAllocationMode] = useState<AllocationMode>(AllocationMode.BY_CLIENT);

  const { actTeamId, clientAllocationDate: allocationDate } = useParams<IParams>();

  const currentUser = useStoreState(state => state.user.current);
  const locationGroups = useStoreState(state => state.clientAllocation.locationGroups);
  const role = useRole();

  const { isLoading, teamMembers, allocations, clients, refetch } = useGetEditClientAllocations({
    actTeamId,
    clientAllocationDate: allocationDate,
    crisisAccess: !!currentUser.crisisAccess || !!role?.team?.isCrisis,
  });

  const showAllocationModeRadio = useMemo(() => {
    const activeAllocations = allocations.filter(a => !a.isArchived);
    const hasLocationGroup = activeAllocations.some(a => a.locationGroups.length > 0);
    const hasClient = activeAllocations.some(a => a.locationGroups.length === 0);
    return hasLocationGroup && hasClient;
  }, [allocations]);

  useEffect(() => {
    const currentAllocationMode = allocations.some(
      allocation => !allocation.isArchived && allocation.locationGroups.length > 0,
    )
      ? AllocationMode.BY_LOCATION_GROUP
      : AllocationMode.BY_CLIENT;

    if (allocationMode !== currentAllocationMode) {
      setAllocationMode(currentAllocationMode);
    }
  }, [allocationMode, allocations]);

  const activeTeamMembers = useMemo(
    () => teamMembers?.filter(member => !member.isDeleted) || [],
    [teamMembers],
  );

  const normalizedAllocationsByUser = useMemo(
    () => normalizeAllocationsByUser(allocations, activeTeamMembers),
    [allocations, activeTeamMembers],
  );

  const state = useMemo(
    () => ({
      isLoading,
      teamMembers: activeTeamMembers,
      allocations,
      clients,
      allocationMode,
      setAllocationMode,
      showAllocationModeRadio,
      refetch,
      actTeamId,
      allocationDate,
      clinicId: currentUser?.clinic?.id ? String(currentUser.clinic.id) : null,
      currentUserRole: role,
      locationGroups,
      normalizedAllocationsByUser,
    }),
    [
      isLoading,
      activeTeamMembers,
      allocations,
      clients,
      allocationMode,
      showAllocationModeRadio,
      refetch,
      actTeamId,
      allocationDate,
      currentUser?.clinic?.id,
      role,
      locationGroups,
      normalizedAllocationsByUser,
    ],
  );

  return (
    <EditClientAllocationContext.Provider value={state}>
      {children}
    </EditClientAllocationContext.Provider>
  );
};

export const useEditClientAllocationContext = (): EditClientAllocationContextValue => {
  const context = useContext(EditClientAllocationContext);

  if (!context) {
    throw new Error(
      'useEditClientAllocationContext must be used within EditClientAllocationProvider',
    );
  }

  return context;
};
